import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {ColumnMode, DatatableComponent, SelectionType, SortType} from '@almaobservatory/ngx-datatable';
import {debounceTime, filter, filter as filterRxjs, map, switchMap, tap} from 'rxjs/operators';
import {BehaviorSubject, combineLatest, merge, Observable, Subject} from 'rxjs';
import {ColumnFilter, ColumnFiltersService} from '../columns-filters/column-filters.service';
import {ColumnsService} from '../columns-picker/columns.service';
import {SearchTableService} from '../search-table.service';
import {ExportRowsEvent} from '../export/export-container/export-container.component';
import {TableExportService} from '../export/table-export.service';
import * as FileSaver from 'file-saver';
import {SampService} from '../samp/samp.service';
import {AsaxDownloadingService, DownloadModalComponent, DownloadPreparingDataModalComponent} from 'asax-downloading';
import {ElasticFiltersService} from 'asax-elastic';
import {
  AsaxFrequencyViewService,
  AsaxHeaderService,
  AsaxResultTableService,
  AsaxSkyViewService,
  Column,
  Extremes,
  Footprint,
  HeaderFilter,
  Observation,
  Project,
  Publication,
  AsaxScrollingService
} from 'asax-common';
import moment from 'moment';
import {SampExportEvent} from '../samp/samp-container/samp-container.component';
import {AsaxBookmarkingService} from 'asax-bookmarking';
import {isEqual} from 'lodash';
import {SearchTableAnalyticsService} from "../search-table-analytics.service";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {HttpClient} from "@angular/common/http";
import {AsaxPropertiesService} from "asax-properties";

const footprintSampColumn: Column = {
  name: 's_region',
  prop: 'footprint',
  selected: true,
  dataType: 'char',
  xType: 'adql:CHAR',
  arraySize: '*'
};

const canRowBeSelected = ({releaseDate}) => !!releaseDate && !releaseDate.toString().startsWith('3');
const buildColumnsNamesList = (columns: Column[]): string[] => columns.map(column => column.name);
const buildNonFrozenLeftColumnsNamesList = (columns: Column[]): string[] => columns.filter(column => column?.frozenLeft !== true).map(column => column.name);
const buildNonFrozenLeftSelectedColumnsNamesList = (columns: Column[]): string[] => columns.filter(column => column?.frozenLeft !== true).filter(column => !column?.selected).map(column => column.name);

const suffixSortProp = `SortProp`;
const suffixSortDir = `SortDir`;

const suffixColumnsOrder = `ColumnsOrder`;
const suffixColumnsHidden = `ColumnsHidden`;

@Component({
  selector: 'asax-result-table-search-table-container',
  templateUrl: './table-container.component.html',
  providers: [ColumnFiltersService, ColumnsService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./index.scss', './material.scss', './loading-bar.scss', './table-container.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TableContainerComponent implements OnInit, OnChanges, AfterContentInit {
  readonly pageSizeLimit = 50;
  readonly headerHeight = 55;
  readonly rowHeight = 31;

  @ViewChild(DatatableComponent, {static: true}) datatableComponent: DatatableComponent;
  @ViewChild('filtersContainer', { read: ElementRef }) public filtersContainer: ElementRef<any>;

  @Input() id: string;
  @Input() height: number;
  @Input() width: number;
  @Input() columns: Column[];
  @Input() index: string;
  @Input() headerSearchFiltersMappingSearchProperties: any;
  @Input() columnsSearchFiltersMappingSearchProperties: any;

  @Input() positionAggregation: any;
  @Input() frequencyRangeAggregation: any;
  @Input() redshiftAggregation: any;

  @Output() outTotalRows = new EventEmitter<number>();

  SortType = SortType;
  ColumnMode = ColumnMode;
  SelectionType = SelectionType;

  headerFilters: HeaderFilter[] = [];
  columnFilters: ColumnFilter[] = [];
  elasticDSLQueryBody: any = {};
  elasticDSLAggregations: any = {};

  totalRows = 0;
  rows: Array<Observation | Project | Publication> = [];
  allRows: Array<Observation | Project | Publication> = [];

  selectedRowsIds: string[] = [];
  areAllRowsSelected: boolean;

  sortElastic: any = {releaseDate: {order: 'asc'}};
  sortNgxDataTable: any = {prop: 'releaseDate', dir: 'asc'};
  sortSubject = new BehaviorSubject<any>(this.sortElastic);

  tableScrollOffset: number;

  columnsSubject = new BehaviorSubject<any[]>([]);
  searchRowsSubject = new Subject();
  searchAllRowsSubject = new Subject();
  searchAggregationsSubject = new Subject();

  columnsObservable: Observable<any[]>;
  columnFiltersObservable: Observable<ColumnFilter[]>;
  columnsForColumnsPicker: Column[];
  displayRemove: Observable<boolean>;

  isExportFileBeingGenerated = false;
  isSampFileBeingGenerated = false;

  extremes: Extremes;
  raDecFov: [number, number, number];
  redshiftEstimated: number;

  aladinHoveredFootprint: Footprint;

  queryParamSortProp: string;
  queryParamSortDir: string;

  queryParamColumnsOrder: string;
  queryParamColumnsHidden: string;

  isLoading: boolean;

  now = moment().format('YYYY-MM-DD');
  
  canScrollLeft: Observable<boolean>;
  canScrollRight: Observable<boolean>;

  rowIdentity = (row) => row.id;
  rowClass = (row): any => ({
    hover: this.aladinHoveredFootprint?.datasetId === row.id,
    active: this.isRowSelected(row)
  });

  downloadSubject = new Subject();

  modalPreparingDataRef: NgbModalRef;
  modalDownloadRef: NgbModalRef;

  modalPreparingDataCancel: boolean;
  rhParamsValue: string = '';

  firstWithValueIndex: number | null = null;

  constructor(private readonly ref: ChangeDetectorRef,
              private readonly searchTableService: SearchTableService,
              private readonly columnFiltersService: ColumnFiltersService,
              private readonly columnsService: ColumnsService,
              private readonly elasticFiltersService: ElasticFiltersService,
              private readonly tableExportService: TableExportService,
              private readonly sampService: SampService,
              private readonly asaxBookmarkingService: AsaxBookmarkingService,
              private readonly asaxDownloadingService: AsaxDownloadingService,
              private readonly asaxHeaderService: AsaxHeaderService,
              private readonly asaxResultTableService: AsaxResultTableService,
              private readonly asaxSkyViewService: AsaxSkyViewService,
              private readonly searchTableAnalyticsService: SearchTableAnalyticsService,
              private readonly asaxFrequencyViewService: AsaxFrequencyViewService,
              private readonly modalService: NgbModal,
              private readonly httpClient: HttpClient,
              public readonly asaxPropertiesService: AsaxPropertiesService,
              public readonly scrollingService: AsaxScrollingService) {

    this.canScrollLeft = this.scrollingService.getTableCanScrollLeft();
    this.canScrollRight = this.scrollingService.getTableanScrollRight();
  }

  ngOnInit(): void {
    this.datatableComponent?.element?.querySelector('datatable-body')?.addEventListener('mouseleave', () => this.asaxResultTableService.setRowHovered(undefined));

    this.queryParamSortProp = `${this.id}${suffixSortProp}`;
    this.queryParamSortDir = `${this.id}${suffixSortDir}`;

    this.queryParamColumnsOrder = `${this.id}${suffixColumnsOrder}`;
    this.queryParamColumnsHidden = `${this.id}${suffixColumnsHidden}`;

    this.asaxBookmarkingService.getQueryParams().pipe(
      map(queryParams => ({prop: queryParams[this.queryParamSortProp], dir: queryParams[this.queryParamSortDir]})),
      map(({prop, dir}) => ({prop: prop || this.sortNgxDataTable.prop, dir: dir || this.sortNgxDataTable.dir})),
      tap(sort => this.sortSubject.next(sort))
    ).subscribe();
    
    if (this.id == 'observations') {
      this.asaxBookmarkingService.getQueryParam(this.asaxDownloadingService.requestDataPathStart).pipe(
        debounceTime(500),
        tap(() => this.modalPreparingDataCancel = false),
        tap(() => this.modalPreparingDataRef = this.modalService.open(DownloadPreparingDataModalComponent, {centered: true, backdrop: 'static'})),
        tap(() => this.modalPreparingDataRef?.dismissed.pipe(tap(() => this.modalPreparingDataCancel = true)).subscribe()),
        tap(() => {
          this.rhParamsValue = decodeURI(window.location.search).split(this.asaxDownloadingService.requestDataPathEnd)[1];
        }),
        filter(Boolean), //Filter value different than null or undfined
        switchMap((value) => this.asaxDownloadingService.buildRequestFromPath(value)),
        switchMap((data) => this.httpClient.post<any>(`${this.asaxPropertiesService.getProperties().requestHandlerNewUrl}/requests`, data)),
        tap(() => this.modalPreparingDataRef?.close()),
        filter(() => !this.modalPreparingDataCancel),
        map((request) => {
          if (this.rhParamsValue) {
            return `${this.asaxPropertiesService.getProperties().requestHandlerNewUrl}/?request=${request?.id}${this.rhParamsValue}`;
          }
            return `${this.asaxPropertiesService.getProperties().requestHandlerNewUrl}/?request=${request?.id}`;
        }),
        tap(() => this.modalDownloadRef = this.modalService.open(DownloadModalComponent, {centered: true, windowClass: 'download-modal'})),
        tap((url) => this.modalDownloadRef.componentInstance.src = url),
        map(() => this.asaxDownloadingService.configureAqPath()),
        tap((path) => this.asaxBookmarkingService.removeNotAllowedParams(this.asaxBookmarkingService.getQueryParamsFromPath(path)))
      ).subscribe();
    }
    

    this.sortSubject.asObservable().pipe(
      filterRxjs(({prop, dir}) => prop !== this.sortNgxDataTable.prop || dir !== this.sortNgxDataTable.dir),
      tap(sort => this.sortNgxDataTable = sort),
      tap(sort => this.sortElastic = {[sort.prop]: {order: sort.dir}}),
      tap(() => this.searchRowsSubject.next(undefined)),
      tap(() => this.searchAllRowsSubject.next(undefined))
    ).subscribe();

    this.columnFiltersObservable = this.columnFiltersService.getColumnFilters();
    this.columnFiltersObservable.subscribe(filters => {
      this.firstWithValueIndex = filters.findIndex(filter => filter.value !== null && filter.value !== undefined && filter.value !== '');
    });

    this.columnFiltersService.getColumnFilters().pipe(
      tap(() => this.scrollingService.delayedCheckScroll(this.filtersContainer, 'TableContainerComponent'))
    ).subscribe();

    this.columnFiltersService.getColumnFilters()

    this.columnsService.setDefaultColumns(this.columns);

    this.columnsService.getColumns().pipe(
      debounceTime(500),
      filterRxjs(columns => columns !== undefined),
      tap((columns) => this.addQueryParamColumnsOrderToUrl([...columns])),
      tap((columns) => this.addQueryParamColumnsHiddenToUrl([...columns]))
    ).subscribe();

    this.columnsObservable = this.columnsService.getColumns().pipe(
      filterRxjs(columns => columns !== undefined),
      map(columns => columns.filter(column => column.selected)),
      filterRxjs((columns) => {
        const currentNonFrozenLeftVisibleColumns = this.datatableComponent?._internalColumns?.filter(column => !column.frozenLeft).map(column => column.name);
        const newNonFrozenLeftVisibleColumns = columns.filter(column => !column.frozenLeft).map(column => column.name);

        return !isEqual(currentNonFrozenLeftVisibleColumns, newNonFrozenLeftVisibleColumns);
      })
    );

    merge(
      this.asaxHeaderService.getHeaderFilters().pipe(tap(headerFilters => this.headerFilters = headerFilters)),
      this.columnFiltersService.getColumnFilters().pipe(tap(filters => this.columnFilters = filters))
    ).pipe(
      tap(() => this.datatableComponent.element.querySelector('datatable-body').scrollTop = 0),
      tap(() => this.buildElasticDSLQueryBodyFromFilters()),
      tap(() => this.buildElasticDSLAggregationsFromAggregations()),
      tap(() => this.searchRowsSubject.next(undefined)),
      tap(() => this.searchAllRowsSubject.next(undefined)),
      tap(() => this.searchAggregationsSubject.next(undefined))
    ).subscribe();

    this.asaxSkyViewService.getFootprintHovered().pipe(
      debounceTime(1000),
      filterRxjs(() => this.asaxResultTableService.getActiveTabIdValue() === 'observations'),
      tap(footprint => this.aladinHoveredFootprint = footprint),
      tap(() => this.updateTableView()),
      filterRxjs(footprint => !!footprint),
      map(footprint => this.allRows?.findIndex(row => row.id === footprint.datasetId)),
      filterRxjs(rowIndex => rowIndex > -1),
      tap(rowIndex => this.datatableComponent.element.querySelector('datatable-body').scrollTop = rowIndex * this.rowHeight),
      tap(() => this.updateTableView())
    ).subscribe();

    combineLatest([
      this.columnFiltersService.getColumnFilters(),
      this.asaxResultTableService.getActiveTabId().pipe(filterRxjs(activeTabId => activeTabId === this.id))
    ]).pipe(
      tap(([columnFilters, activeTab]) => this.asaxResultTableService.setColumnsFiltersForActiveTabSubject(columnFilters))
    ).subscribe();

    this.displayRemove = this.columnFiltersService.getColumnFilters().pipe(
      map(filters => filters.filter(filter => !!filter?.value)),
      map(filters => filters?.length > 0)
    );

    this.downloadSubject.asObservable().pipe(
      tap(() => this.modalPreparingDataCancel = false),
      tap(() => this.modalPreparingDataRef = this.modalService.open(DownloadPreparingDataModalComponent, {
        centered: true,
        backdrop: 'static'
      })),
      tap(() => this.modalPreparingDataRef?.dismissed.pipe(tap(() => this.modalPreparingDataCancel = true)).subscribe()),
      switchMap((rowId: string) => {
        switch (this.id) {
          case 'observations': {
            return this.asaxDownloadingService.buildRequestToDownloadFromObservation(rowId);
          }
          case 'projects': {
            return this.asaxDownloadingService.buildRequestToDownloadFromProject(rowId);

          }
          case 'publications': {
            return this.asaxDownloadingService.buildRequestToDownloadFromPublication(rowId);

          }
        }
      }),
      switchMap((data) => this.httpClient.post<any>(`${this.asaxPropertiesService.getProperties().requestHandlerNewUrl}/requests`, data)),
      tap(() => this.modalPreparingDataRef?.close()),
      filter(() => !this.modalPreparingDataCancel),
      map((request) => `${this.asaxPropertiesService.getProperties().requestHandlerNewUrl}/?request=${request?.id}`),
      tap(() => this.modalDownloadRef = this.modalService.open(DownloadModalComponent, {
        centered: true,
        windowClass: 'download-modal'
      })),
      tap((url) => this.modalDownloadRef.componentInstance.src = url),
    ).subscribe();
  }

  ngAfterContentInit(): void {
    this.asaxBookmarkingService.getQueryParams().pipe(
      map(queryParams => ({
        columnsOrder: queryParams[this.queryParamColumnsOrder]?.split(',') || buildColumnsNamesList(this.columns),
        columnsHidden: queryParams[this.queryParamColumnsHidden]?.split(',')
      })),
      map(({columnsOrder, columnsHidden}) => {
        const columnsFrozenLeft = this.columns.filter(column => column.frozenLeft);
        const columnsNonFrozenLeft = this.columns.filter(column => !column.frozenLeft);
        const columns = columnsOrder.map(columnName => columnsNonFrozenLeft.find(columnNonFrozenLeft => columnNonFrozenLeft.name === columnName))
          .filter(column => !!column)
          .map(column => ({...column, selected: columnsHidden?.find(name => name === column.name) ? false : true}));
        return [...columnsFrozenLeft, ...columns];
      }),
      filterRxjs(columns => !isEqual(columns, this.columnsService.getColumnsValue())),
      tap((columns) => this.columnsService.setColumns([...columns]))
    ).subscribe();

    this.searchRowsSubject.asObservable().pipe(
      debounceTime(500),
      map(() => ({
        index: this.index,
        from: this.tableScrollOffset,
        size: this.pageSizeLimit,
        source: ['id', 'datasetId', ...this.columns.map(column => [column.prop, column.props || []]).flat(2)],
        sort: this.sortElastic,
        query: this.elasticDSLQueryBody
      })),
      tap(() => this.isLoading = true),
      tap(() => this.ref.markForCheck()),
      switchMap(searchTablePage => this.searchTableService.search(searchTablePage)),
      tap(searchResultTablePage => this.totalRows = searchResultTablePage.totalRows),
      tap(() => this.outTotalRows.emit(this.totalRows)),
      tap(searchResultTablePage => {
        const rows = new Array(searchResultTablePage.from);
        rows.splice(searchResultTablePage.from, 0, ...searchResultTablePage.rows);
        this.rows = rows;
      }),
      tap(() => this.isLoading = false),
      tap(() => this.ref.markForCheck()),
      filterRxjs(() => this.asaxResultTableService.getActiveTabIdValue() === this.id),
      tap(() => this.searchTableAnalyticsService.log([...this.headerFilters, ...this.columnFilters]))
    ).subscribe();

    this.searchAllRowsSubject.asObservable().pipe(
      debounceTime(1500),
      tap(() => this.allRows = undefined),
      map(() => ({
        index: this.index,
        source: ['id', 'releaseDate'],
        sort: this.sortElastic,
        query: this.elasticDSLQueryBody
      })),
      switchMap(searchTablePage => this.searchTableService.searchAllRows(searchTablePage)),
      tap(rows => this.allRows = rows),
      tap(() => this.ref.markForCheck())
    ).subscribe();

    this.searchAggregationsSubject.asObservable().pipe(
      debounceTime(1000),
      map(() => ({index: this.index, query: this.elasticDSLQueryBody, aggregations: this.elasticDSLAggregations})),
      switchMap(searchTablePage => this.searchTableService.searchAggregations(searchTablePage)),
      tap((aggregations) => this.setRaDecFovFromAggregations(aggregations)),
      tap((aggregations) => this.setExtremesFromAggregations(aggregations)),
      tap((aggregations) => this.setRedshiftEstimatedFromAggregations(aggregations)),
      filterRxjs(() => this.asaxResultTableService.getActiveTabIdValue() === this.id),
      tap(() => this.setSkyViewRaDecFov(this.raDecFov)),
      tap(() => this.setFrequencyViewExtremes(this.extremes)),
      tap(() => this.setRedShiftEstimated(this.redshiftEstimated))
    ).subscribe();

    merge(
      this.asaxDownloadingService.getObservationsToBeDownloaded().pipe(filterRxjs(() => this.id === 'observations')),
      this.asaxDownloadingService.getProjectsToBeDownloaded().pipe(filterRxjs(() => this.id === 'projects')),
      this.asaxDownloadingService.getPublicationsToBeDownloaded().pipe(filterRxjs(() => this.id === 'publications')),
    ).pipe(
      tap(rowIdsToBeDownloaded => this.selectedRowsIds = [...rowIdsToBeDownloaded]),
      tap(() => this.setAreAllRowsSelected()),
      tap(() => this.updateTableView())
    ).subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (((changes.height && this.height) || (changes.width && this.width))) {
      this.updateTableView();
    }
  }

  onPage($event: any): void {
    const from = ($event.offset * $event.pageSize) - Math.floor(this.pageSizeLimit / 2);
    this.tableScrollOffset = (from < 0) ? 0 : from;

    this.searchRowsSubject.next(undefined);
  }

  onSort({sorts}: any): void {
    const {prop, dir} = sorts[0];
    this.sortElastic = {[prop]: {order: dir}};
    this.sortNgxDataTable = {prop, dir};

    this.searchRowsSubject.next(undefined);
    this.searchAllRowsSubject.next(undefined);
    this.asaxBookmarkingService.addParamsToUrl({[this.queryParamSortProp]: prop, [this.queryParamSortDir]: dir});
  }

  onReorder({column}): void {
    const columnReorder = this.columns.find(c => c.name === column.name);
    const indexOfReorderedColumnInCurrentTable = this.datatableComponent?._internalColumns.findIndex(c => c.name === column.name);
    const prevColumnToReorderedColumnInCurrentTable = this.datatableComponent?._internalColumns[indexOfReorderedColumnInCurrentTable - 1];

    const columns = this.columnsService.getColumnsValue();
    const indexOfReorderedColumnInColumns = columns.findIndex(c => c.name === column.name);
    columns.splice(indexOfReorderedColumnInColumns, 1);

    const indexOfPrevColumnToReorderedColumnInColumns = columns.findIndex(c => c.name === prevColumnToReorderedColumnInCurrentTable.name);
    columns.splice(indexOfPrevColumnToReorderedColumnInColumns + 1, 0, columnReorder);

    this.columnsService.setColumns([...columns]);
  }

  onActivate({row}): void {
    this.asaxResultTableService.setRowHovered(row);
  }

  updateTableView(): void {
    setTimeout(() => {
      this.rows = [...this.rows];
      this.datatableComponent?.recalculateDims();
      this.ref.markForCheck();
    });
  }

  onColumnFilter(columnFilter: ColumnFilter): void {
    this.columnFiltersService.upsertColumnFilter(columnFilter);
  }

  onRemoveColumnFilter(filterId: string): void {
    this.columnFiltersService.removeColumnFilter(filterId);
  }

  removeColumnFilters(): void {
    this.columnFiltersService.removeColumnFilters();
  }

  

  isRowSelected(row: any): boolean {
    switch (this.id) {
      case 'observations': {
        return this.asaxDownloadingService.isObservationSelectedToBeDownloaded(row.id);
        break;
      }
      case 'projects': {
        return this.asaxDownloadingService.isProjectSelectedToBeDownloaded(row.id);
        break;
      }
      case 'publications': {
        return this.asaxDownloadingService.isPublicationSelectedToBeDownloaded(row.id);
        break;
      }
    }
  }

  toggleSelectedRow(row: any): void {
    switch (this.id) {
      case 'observations': {
        if (this.asaxDownloadingService.isObservationSelectedToBeDownloaded(row.id)) {
          this.asaxDownloadingService.removeObservationsToBeDownloaded(row.id);
        } else {
          this.asaxDownloadingService.addObservationsToBeDownloaded(row.id);
        }
        break;
      }
      case 'projects': {
        if (this.asaxDownloadingService.isProjectSelectedToBeDownloaded(row.id)) {
          this.asaxDownloadingService.removeProjectsToBeDownloaded(row.id);
        } else {
          this.asaxDownloadingService.addProjectsToBeDownloaded(row.id);
        }
        break;
      }
      case 'publications': {
        if (this.asaxDownloadingService.isPublicationSelectedToBeDownloaded(row.id)) {
          this.asaxDownloadingService.removePublicationsToBeDownloaded(row.id);
        } else {
          this.asaxDownloadingService.addPublicationsToBeDownloaded(row.id);
        }
        break;
      }
    }
  }

  toggleAllSelectedRows(checked: boolean): void {
    if (checked) {
      const ids = this.allRows?.filter(canRowBeSelected).map(({id}) => id);

      switch (this.id) {
        case 'observations': {
          return this.asaxDownloadingService.addObservationsToBeDownloaded(ids);
          break;
        }
        case 'projects': {
          return this.asaxDownloadingService.addProjectsToBeDownloaded(ids);
          break;
        }
        case 'publications': {
          return this.asaxDownloadingService.addPublicationsToBeDownloaded(ids);
          break;
        }
      }
    } else {
      const ids = this.allRows?.map(({id}) => id);

      switch (this.id) {
        case 'observations': {
          return this.asaxDownloadingService.removeObservationsToBeDownloaded(ids);
          break;
        }
        case 'projects': {
          return this.asaxDownloadingService.removeProjectsToBeDownloaded(ids);
          break;
        }
        case 'publications': {
          return this.asaxDownloadingService.removePublicationsToBeDownloaded(ids);
          break;
        }
      }
    }
  }

  setAreAllRowsSelected(): void {
    const numberOfRowsThatCanBeSelected = this.allRows?.filter(canRowBeSelected).length;
    let numberOfRowsThatAreSelected = 0;

    switch (this.id) {
      case 'observations': {
        numberOfRowsThatAreSelected = this.allRows?.filter(row => this.asaxDownloadingService.isObservationSelectedToBeDownloaded(row.id)).length;
        break;
      }
      case 'projects': {
        numberOfRowsThatAreSelected = this.allRows?.filter(row => this.asaxDownloadingService.isProjectSelectedToBeDownloaded(row.id)).length;
        break;
      }
      case 'publications': {
        numberOfRowsThatAreSelected = this.allRows?.filter(row => this.asaxDownloadingService.isPublicationSelectedToBeDownloaded(row.id)).length;
        break;
      }
    }

    this.areAllRowsSelected = (numberOfRowsThatAreSelected > 0 && numberOfRowsThatCanBeSelected === numberOfRowsThatAreSelected);
  }

  onExportRows(exportRowsEvent: ExportRowsEvent): void {
    this.setIsExportFileBeingGenerated(true);

    const selectedColumns = this.columnsService.getColumnsValue().filter(column => column.selected);
    const columnsProps = selectedColumns.map(column => column.propsToBeExported || column.prop).flat();
    const exportRows = (rows) => this.tableExportService.export(rows, selectedColumns, exportRowsEvent.format);
    const downloadFile = (content) => {
      const blob = new Blob([content], {type: exportRowsEvent.type});
      (FileSaver as any).saveAs(blob, `${this.id}_${moment.now()}.${exportRowsEvent.format}`);
    };

    if (exportRowsEvent.selectionType === 'all-rows') {
      const searchTablePage = {
        index: this.index,
        source: columnsProps,
        sort: this.sortElastic,
        query: this.elasticDSLQueryBody
      };
      this.searchTableService.searchAllRowsByChunks(searchTablePage, this.totalRows).pipe(
        switchMap(exportRows),
        tap(downloadFile),
        tap(() => this.setIsExportFileBeingGenerated(false))
      ).subscribe();
    } else if (exportRowsEvent.selectionType === 'selected-rows') {
      this.searchTableService.searchAllRowsByIds(this.index, this.selectedRowsIds, columnsProps).pipe(
        switchMap(exportRows),
        tap(downloadFile),
        tap(() => this.setIsExportFileBeingGenerated(false))
      ).subscribe();
    }
  }

  setIsExportFileBeingGenerated(isExportFileBeingGenerated: boolean): void {
    setTimeout(() => {
      this.isExportFileBeingGenerated = isExportFileBeingGenerated;
      this.ref.markForCheck();
    });
  }

  onSampExport(sampExportEvent: SampExportEvent): void {
    this.setIsSampFileBeingGenerated(true);
    this.sampService.setSampError(undefined);

    const selectedColumns = [...this.columnsService.getColumnsValue().filter(column => column.selected), footprintSampColumn];
    const columnsProps = selectedColumns.map(column => column.propsToBeExported || column.prop).flat();

    if (sampExportEvent.selectionType === 'all-rows') {
      const searchTablePage = {
        index: this.index,
        source: columnsProps,
        sort: this.sortElastic,
        query: this.elasticDSLQueryBody
      };
      this.searchTableService.searchAllRowsByChunks(searchTablePage, this.totalRows).pipe(
        switchMap((rows) => this.tableExportService.export(rows, selectedColumns, sampExportEvent.format)),
        switchMap(content => this.sampService.sendToSamp(content)),
        tap(() => this.setIsSampFileBeingGenerated(false))
      ).subscribe();
    } else if (sampExportEvent.selectionType === 'selected-rows') {
      this.searchTableService.searchAllRowsByIds(this.index, this.selectedRowsIds, columnsProps).pipe(
        switchMap((rows) => this.tableExportService.export(rows, selectedColumns, sampExportEvent.format)),
        switchMap(content => this.sampService.sendToSamp(content)),
        tap(() => this.setIsSampFileBeingGenerated(false))
      ).subscribe();
    }
  }

  setIsSampFileBeingGenerated(isSampFileBeingGenerated: boolean): void {
    setTimeout(() => {
      this.isSampFileBeingGenerated = isSampFileBeingGenerated;
      this.ref.markForCheck();
    });
  }

  setRedshiftEstimatedFromAggregations(aggregations: any): void {
    const {redshiftAvg} = aggregations?.redshift || aggregations;
    this.redshiftEstimated = +redshiftAvg?.values['50.0'] || 0;
  }

  setRedShiftEstimated(redshift: number): void {
    this.asaxFrequencyViewService.setRedshiftEstimated(redshift);
  }

  setFrequencyViewExtremes(extremes: Extremes): void {
    this.asaxFrequencyViewService.setExtremes(extremes);
  }

  setExtremesFromAggregations(aggregations: any): void {
    const from = aggregations?.frequencyRange?.freqMin?.value;
    const to = aggregations?.frequencyRange?.freqMax?.value;
    this.extremes = {from, to};
  }

  setFrequencyChartExtremesForTable(): void {
    this.setFrequencyViewExtremes({...this.extremes});
  }

  setFrequencyChartExtremesForRow(row: any): void {
    if (row?.code) {
      this.searchTableService.searchAggregations({
        index: this.index,
        query: {term: {code: row?.code}},
        aggregations: this.frequencyRangeAggregation
      }).pipe(
        tap(aggregations => {
          const from = aggregations?.frequencyRange?.freqMin?.value;
          const to = aggregations?.frequencyRange?.freqMax?.value;
          this.setFrequencyViewExtremes({from, to});
        })
      ).subscribe();
    } else if (row?.bibCode) {
      this.searchTableService.searchAggregations({
        index: this.index,
        query: {term: {bibCode: row?.bibCode}},
        aggregations: this.frequencyRangeAggregation
      }).pipe(
        tap(aggregations => {
          const from = aggregations?.frequencyRange?.freqMin?.value;
          const to = aggregations?.frequencyRange?.freqMax?.value;
          this.setFrequencyViewExtremes({from, to});
        })
      ).subscribe();
    } else if (row?.datasetId) {
      this.searchTableService.searchAggregations({
        index: this.index,
        query: {term: {datasetId: row?.datasetId}},
        aggregations: this.frequencyRangeAggregation
      }).pipe(
        tap(aggregations => {
          const from = aggregations?.frequencyRange?.freqMin?.value;
          const to = aggregations?.frequencyRange?.freqMax?.value;
          this.setFrequencyViewExtremes({from, to});
        })
      ).subscribe();
    }
  }

  setSkyViewRaDecFov(raDecFov: [number, number, number]): void {
    this.asaxSkyViewService.setRaDecFov(raDecFov);
  }

  setRaDecFovFromAggregations(aggregations: any): void {
    const {raMin, raMax, ra180Min, ra180Max, decMin, decMax} = aggregations?.position || aggregations;
    const centerAndFOVAggs = findCenterAndFOVAggs(raMin?.value, raMax?.value, ra180Min?.value, ra180Max?.value, decMin?.value, decMax?.value);
    this.raDecFov = [centerAndFOVAggs.ra, centerAndFOVAggs.dec, centerAndFOVAggs.fov];
  }

  centerSkyInRaDecFovForTable(): void {
    this.setSkyViewRaDecFov([...this.raDecFov]);
  }

  centerSkyInRaDecFovForRow(row: any): void {
    if (row?.code) {
      this.searchTableService.searchAggregations({
        index: this.index,
        query: {term: {code: row?.code}},
        aggregations: this.positionAggregation
      }).pipe(
        tap(aggregations => {
          const {raMin, raMax, ra180Min, ra180Max, decMin, decMax} = aggregations?.position;
          const centerAndFOVAggs = findCenterAndFOVAggs(raMin?.value, raMax?.value, ra180Min?.value, ra180Max?.value, decMin?.value, decMax?.value);
          this.setSkyViewRaDecFov([centerAndFOVAggs.ra, centerAndFOVAggs.dec, centerAndFOVAggs.fov]);
        })
      ).subscribe();
    } else if (row?.bibCode) {
      this.searchTableService.searchAggregations({
        index: this.index,
        query: {term: {bibCode: row?.bibCode}},
        aggregations: this.positionAggregation
      }).pipe(
        tap(aggregations => {
          const {raMin, raMax, ra180Min, ra180Max, decMin, decMax} = aggregations?.position;
          const centerAndFOVAggs = findCenterAndFOVAggs(raMin?.value, raMax?.value, ra180Min?.value, ra180Max?.value, decMin?.value, decMax?.value);
          this.setSkyViewRaDecFov([centerAndFOVAggs.ra, centerAndFOVAggs.dec, centerAndFOVAggs.fov]);
        })
      ).subscribe();
    } else if (row?.datasetId) {
      this.searchTableService.searchAggregations({
        index: this.index,
        query: {term: {datasetId: row?.datasetId}},
        aggregations: this.positionAggregation
      }).pipe(
        tap(position => {
          const {raMin, raMax, ra180Min, ra180Max, decMin, decMax} = position;
          const centerAndFOVAggs = findCenterAndFOVAggs(raMin?.value, raMax?.value, ra180Min?.value, ra180Max?.value, decMin?.value, decMax?.value);
          this.setSkyViewRaDecFov([centerAndFOVAggs.ra, centerAndFOVAggs.dec, centerAndFOVAggs.fov]);
        })
      ).subscribe();
    }
  }

  private addQueryParamColumnsOrderToUrl(columns: Column[]): void {
    const defaultColumnsOrder = buildNonFrozenLeftColumnsNamesList(this.columns);
    const columnsOrder = buildNonFrozenLeftColumnsNamesList(columns);

    if (columnsOrder?.length > 0 && !isEqual(defaultColumnsOrder, columnsOrder)) {
      this.asaxBookmarkingService.addParamsToUrl({[this.queryParamColumnsOrder]: columnsOrder.join(',')});
    } else {
      this.asaxBookmarkingService.removeParamsFromUrl(this.queryParamColumnsOrder);
    }
  }

  private addQueryParamColumnsHiddenToUrl(columns: Column[]): void {
    const columnsHidden = buildNonFrozenLeftSelectedColumnsNamesList(columns);

    if (columnsHidden?.length > 0) {
      this.asaxBookmarkingService.addParamsToUrl({[this.queryParamColumnsHidden]: columnsHidden.join(',')});
    } else {
      this.asaxBookmarkingService.removeParamsFromUrl(this.queryParamColumnsHidden);
    }
  }

  private buildElasticDSLQueryBodyFromFilters(): void {
    const filters = [...this.headerFilters, ...this.columnFilters];
    const filtersMappingSearchProperties = {...this.headerSearchFiltersMappingSearchProperties, ...this.columnsSearchFiltersMappingSearchProperties};
    this.elasticDSLQueryBody = this.elasticFiltersService.buildElasticDSLQueryBodyFromFilters(filters, filtersMappingSearchProperties);
  }

  private buildElasticDSLAggregationsFromAggregations(): void {
    const aggregations = [this.positionAggregation, this.frequencyRangeAggregation, this.redshiftAggregation];
    this.elasticDSLAggregations = {};
    aggregations.forEach(agg => {
      for (const key in agg) {
        if (agg.hasOwnProperty(key)) {
          this.elasticDSLAggregations[key] = agg[key];
        }
      }
    });
  }

  downloadRow(row: any) {
    this.downloadSubject.next(row?.datasetId || row?.bibCode || row?.code);
  }

  public scrollRight(): void {
    this.scrollingService.scrollRight(this.filtersContainer, 'TableContainerComponent');
  }

  public scrollLeft(): void {
    this.scrollingService.scrollLeft(this.filtersContainer, 'TableContainerComponent');
  }
}

const findCenterAndFOVAggs = (raMin: number, raMax: number, ra180Min: number, ra180Max: number, decMin: number, decMax: number): any => {
  const piOver180 = 3.1415927 / 180.0;

  const ra0 = [raMin || 0, raMax || 0, 0, 0];
  const ra180 = [ra180Min || 0, ra180Max || 0, 0, 0];
  const dec = [decMin || 0, decMax || 0, 0, 0];

  ra0[2] = 0.5 * (ra0[1] + ra0[0]);
  ra0[3] = ra0[1] - ra0[0];

  ra180[2] = 0.5 * (ra180[1] + ra180[0]);
  ra180[3] = ra180[1] - ra180[0];

  dec[2] = 0.5 * (dec[1] + dec[0]);
  dec[3] = dec[1] - dec[0];

  // Take the ra with the smaller extent
  let ra = Object.assign([], ra0);

  if (ra0[3] > ra180[3]) {
    // Turn ra180 back (forward is better)
    ra180[0] = (ra180[0] + 180.0) % 360.0;
    ra180[1] = (ra180[1] + 180.0) % 360.0;
    ra180[2] = (ra180[2] + 180.0) % 360.0;
    ra = Object.assign([], ra180);
  }

  // Take the larger of the two FOVs: Aladin gets a single value as input.
  let fov = 1.5 * Math.max(dec[3], ra[3] * Math.cos((dec[2] * piOver180)));

  if (fov === 0) {
    fov = 180;
  }

  return {ra: ra[2], dec: dec[2], fov};
};
